export interface PortDefinition {
  id: string;
  type: 'input' | 'output';
  x: number;
  y: number;
}

export interface SVGUnitConfig {
  width: number;
  height: number;
  viewBox: string;
  ports: PortDefinition[];
}

/**
 * Parse dimension string and convert to pixels if needed
 * Assumes 1mm = 3.779528 pixels (96 DPI standard)
 */
function parseDimension(value: string): number {
  if (!value) return 60; // default fallback
  
  const mmMatch = value.match(/^([\d.]+)mm$/);
  if (mmMatch) {
    return parseFloat(mmMatch[1]) * 3.779528; // Convert mm to pixels
  }
  
  // Handle other units or unitless values
  return parseFloat(value);
}

/**
 * Parse SVG content to extract port definitions and dimensions
 */
export function parseSVGPorts(svgContent: string): SVGUnitConfig {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, 'image/svg+xml');
  const svgElement = doc.querySelector('svg');
  
  if (!svgElement) {
    throw new Error('Invalid SVG content');
  }

  // Extract dimensions - handle mm units properly
  const widthAttr = svgElement.getAttribute('width') || '60';
  const heightAttr = svgElement.getAttribute('height') || '60';
  const width = parseDimension(widthAttr);
  const height = parseDimension(heightAttr);
  const viewBox = svgElement.getAttribute('viewBox') || `0 0 ${width} ${height}`;

  // Extract ports from circles with data-port attributes
  const portElements = doc.querySelectorAll('circle[data-port]');
  const ports: PortDefinition[] = Array.from(portElements).map(circle => {
    const cx = parseFloat(circle.getAttribute('cx') || '0');
    const cy = parseFloat(circle.getAttribute('cy') || '0');
    const portType = circle.getAttribute('data-port') as 'input' | 'output';
    const portId = circle.getAttribute('data-port-id') || `${portType}-${cx}-${cy}`;
    
    return {
      id: portId,
      type: portType,
      x: cx,
      y: cy
    };
  });

  return {
    width,
    height,
    viewBox,
    ports
  };
}